import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllPlans,
  updatePlan,
  selectPlans,
  selectLoading
} from '../../../../app/store/slices/adminSlice';
import { CircularProgress } from '@mui/material';
import { FiEdit2, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';

const UNLIMITED = 100;
const safeNum = (v, fallback = 0) => Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : fallback;
const isUnlimited = v => v >= UNLIMITED;

const TierManagement = () => {
  const dispatch = useDispatch();
  const plans = useSelector(selectPlans) || [];
  const loading = useSelector(selectLoading);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchAllPlans());
  }, [dispatch]);

  const hydrateForm = (plan) => {
    const f = plan.features || {};
    const canvas = f.canvas || {};
    const modules = canvas.modules || {};
    const tools = f.tools || {};
    const premium = f.premium || {};
    const display = f.display || {};
    const prices = plan.prices || {};

    return {
      name: plan.name || '',
      adultMax: f.accounts?.adult?.max ?? 1,
      kidMax: f.accounts?.kid?.max ?? 0,
      canvasEnabled: !!canvas.enabled,
      canvasShared: !!canvas.shared,
      canvasMax: canvas.max ?? UNLIMITED,
      
      // Pricing
      monthlyAmount: prices.monthly?.amount ?? 0,
      monthlyDisplay: prices.monthly?.display ?? '',
      quarterlyAmount: prices.quarterly?.amount ?? 0,
      quarterlyDisplay: prices.quarterly?.display ?? '',
      quarterlySave: prices.quarterly?.save ?? '',
      yearlyAmount: prices.yearly?.amount ?? 0,
      yearlyDisplay: prices.yearly?.display ?? '',
      yearlySave: prices.yearly?.save ?? '',
      
      // Modules
      moduleAccountsEnabled: modules.accounts?.enabled ?? true,
      moduleIncomeEnabled: modules.income?.enabled ?? true,
      moduleBudgetEnabled: modules.budget?.enabled ?? true,
      moduleDebtEnabled: modules.debt?.enabled ?? true,
      moduleSavingsEnabled: modules.savings?.enabled ?? true,
      moduleInvestmentEnabled: modules.investment?.enabled ?? true,
      moduleSubscriptionsEnabled: modules.subscriptions?.enabled ?? false,
      moduleMortgageEnabled: modules.mortgage?.enabled ?? false,
      
      // Tools
      insightsEnabled: tools.insights?.enabled ?? true,
      documentsEnabled: tools.documents?.enabled ?? false,
      documentsHasAds: tools.documents?.hasAds ?? false,
      smartWidgetsEnabled: tools.smartWidgets?.enabled ?? false,
      smartWidgetsHasAds: tools.smartWidgets?.hasAds ?? false,
      dashboardEnabled: tools.dashboard?.enabled ?? false,
      dashboardHasAds: tools.dashboard?.hasAds ?? false,
      
      // Premium
      subscriptionTracker: premium.subscriptionTracker ?? false,
      investmentTracker: premium.investmentTracker ?? false,
      cashbot: premium.cashbot ?? false,
      premiumSmartWidgets: premium.smartWidgets ?? false,
      displayHasAds: display.hasAds ?? true
    };
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm(hydrateForm(plan));
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setModalOpen(false);
  };

  const onChange = (field, value) => {
    setForm(f => {
      const newForm = { ...f, [field]: value };
      
      // Auto-format display prices
      if (field === 'monthlyAmount') {
        newForm.monthlyDisplay = value ? `$${parseFloat(value).toFixed(2)}` : '';
      } else if (field === 'quarterlyAmount') {
        newForm.quarterlyDisplay = value ? `$${parseFloat(value).toFixed(2)}` : '';
        // Auto-calculate quarterly save: (monthly * 3) - quarterly
        if (newForm.monthlyAmount && value) {
          const monthlyCost = parseFloat(newForm.monthlyAmount) * 3;
          const quarterlyCost = parseFloat(value);
          const save = monthlyCost - quarterlyCost;
          newForm.quarterlySave = save > 0 ? `$${save.toFixed(2)}` : '$0.00';
        }
      } else if (field === 'yearlyAmount') {
        newForm.yearlyDisplay = value ? `$${parseFloat(value).toFixed(2)}` : '';
        // Auto-calculate yearly save: (monthly * 12) - yearly
        if (newForm.monthlyAmount && value) {
          const monthlyCost = parseFloat(newForm.monthlyAmount) * 12;
          const yearlyCost = parseFloat(value);
          const save = monthlyCost - yearlyCost;
          newForm.yearlySave = save > 0 ? `$${save.toFixed(2)}` : '$0.00';
        }
      }
      
      // Recalculate saves when monthly amount changes
      if (field === 'monthlyAmount') {
        // Recalculate quarterly save
        if (newForm.quarterlyAmount && value) {
          const monthlyCost = parseFloat(value) * 3;
          const quarterlyCost = parseFloat(newForm.quarterlyAmount);
          const save = monthlyCost - quarterlyCost;
          newForm.quarterlySave = save > 0 ? `$${save.toFixed(2)}` : '$0.00';
        }
        
        // Recalculate yearly save
        if (newForm.yearlyAmount && value) {
          const monthlyCost = parseFloat(value) * 12;
          const yearlyCost = parseFloat(newForm.yearlyAmount);
          const save = monthlyCost - yearlyCost;
          newForm.yearlySave = save > 0 ? `$${save.toFixed(2)}` : '$0.00';
        }
      }
      
      return newForm;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        name: form.name,
        prices: {
          monthly: {
            stripePriceId: editingPlan.prices?.monthly?.stripePriceId || '',
            amount: parseFloat(form.monthlyAmount) || 0,
            display: form.monthlyDisplay || `$${form.monthlyAmount}`
          },
          quarterly: {
            stripePriceId: editingPlan.prices?.quarterly?.stripePriceId || '',
            amount: parseFloat(form.quarterlyAmount) || 0,
            display: form.quarterlyDisplay || `$${form.quarterlyAmount}`,
            save: form.quarterlySave || ''
          },
          yearly: {
            stripePriceId: editingPlan.prices?.yearly?.stripePriceId || '',
            amount: parseFloat(form.yearlyAmount) || 0,
            display: form.yearlyDisplay || `$${form.yearlyAmount}`,
            save: form.yearlySave || ''
          }
        },
        features: {
          accounts: {
            adult: { max: safeNum(form.adultMax, 1) },
            kid: { max: safeNum(form.kidMax, 0) }
          },
          canvas: {
            enabled: !!form.canvasEnabled,
            shared: !!form.canvasShared,
            max: safeNum(form.canvasMax, UNLIMITED),
            modules: {
              accounts: { 
                enabled: !!form.moduleAccountsEnabled, 
                max: safeNum(form.canvasMax, UNLIMITED),
                adultMax: safeNum(form.adultMax, 1),
                kidMax: safeNum(form.kidMax, 0)
              },
              income: { enabled: !!form.moduleIncomeEnabled, max: safeNum(form.canvasMax, UNLIMITED) },
              budget: { enabled: !!form.moduleBudgetEnabled, max: safeNum(form.canvasMax, UNLIMITED) },
              debt: { enabled: !!form.moduleDebtEnabled, max: safeNum(form.canvasMax, UNLIMITED) },
              savings: { enabled: !!form.moduleSavingsEnabled, max: safeNum(form.canvasMax, UNLIMITED) },
              investment: { enabled: !!form.moduleInvestmentEnabled, max: safeNum(form.canvasMax, UNLIMITED) },
              subscriptions: { enabled: !!form.moduleSubscriptionsEnabled, max: form.moduleSubscriptionsEnabled ? safeNum(form.canvasMax, UNLIMITED) : 0 },
              mortgage: { enabled: !!form.moduleMortgageEnabled, max: form.moduleMortgageEnabled ? safeNum(form.canvasMax, UNLIMITED) : 0 }
            }
          },
          tools: {
            insights: { enabled: !!form.insightsEnabled, predefined: true, max: safeNum(form.canvasMax, UNLIMITED) },
            documents: { enabled: !!form.documentsEnabled, hasAds: !!form.documentsHasAds, import: true, export: true, maxSize: UNLIMITED },
            smartWidgets: { enabled: !!form.smartWidgetsEnabled, hasAds: !!form.smartWidgetsHasAds, aiInsights: true, max: UNLIMITED },
            dashboard: { enabled: !!form.dashboardEnabled, hasAds: !!form.dashboardHasAds, maxWidgets: UNLIMITED }
          },
          premium: {
            cashbot: !!form.cashbot,
            subscriptionTracker: !!form.subscriptionTracker,
            mortgage: !!form.moduleMortgageEnabled,
            investmentTracker: !!form.investmentTracker,
            smartWidgets: !!form.premiumSmartWidgets
          },
          display: { hasAds: !!form.displayHasAds }
        }
      };
      
  
      
      await dispatch(updatePlan({ id: editingPlan._id, data: payload })).unwrap();
      toast.success('Plan updated successfully');
      setModalOpen(false);
      dispatch(fetchAllPlans());
    } catch (err) {
      console.error('Validation error:', err);
      
      // Better error handling for validation errors
      if (err?.data?.message && Array.isArray(err.data.message)) {
        const errorMessages = err.data.message.join(', ');
        toast.error(`Validation errors: ${errorMessages}`);
      } else {
        toast.error(err?.message || 'Update failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const renderValue = (featureKey, plan) => {
    const f = plan.features || {};
    const accounts = f.accounts || {};
    const canvas = f.canvas || {};
    const modules = canvas.modules || {};
    const tools = f.tools || {};
    const premium = f.premium || {};
    const display = f.display || {};

    const getValue = () => {
      switch (featureKey) {
        case 'adult': return isUnlimited(accounts.adult?.max) ? 'Unlimited' : (accounts.adult?.max ?? '-');
        case 'kids': return isUnlimited(accounts.kid?.max) ? 'Unlimited' : (accounts.kid?.max === 0 ? '0' : (accounts.kid?.max ?? '-'));
        case 'canvas': return canvas.enabled ? 'Unlimited' : 'NO';
        case 'sharedCanvas': return canvas.shared ? 'YES' : 'NO';
        case 'moduleAccounts': return modules.accounts?.enabled ? 'Unlimited' : 'NO';
        case 'moduleIncome': return modules.income?.enabled ? 'YES' : 'NO';
        case 'moduleBudget': return modules.budget?.enabled ? 'YES' : 'NO';
        case 'moduleDebt': return modules.debt?.enabled ? 'YES' : 'NO';
        case 'moduleSavings': return modules.savings?.enabled ? 'YES' : 'NO';
        case 'moduleInvestment': return modules.investment?.enabled ? 'YES' : 'NO';
        case 'moduleSubscriptions': return modules.subscriptions?.enabled ? 'YES' : 'NO';
        case 'moduleMortgage': return modules.mortgage?.enabled ? 'YES' : 'NO';
        case 'predefinedInsights': return tools.insights?.enabled ? 'YES' : 'NO';
        case 'documents': return tools.documents?.enabled ? (tools.documents.hasAds ? 'YES (with ads)' : 'YES') : 'NO';
        case 'smartWidgets': return tools.smartWidgets?.enabled ? (tools.smartWidgets.hasAds ? 'YES (with ads)' : 'YES') : 'NO';
        case 'dashboard': return tools.dashboard?.enabled ? (tools.dashboard.hasAds ? 'YES (with ads)' : 'YES') : 'NO';
        case 'subscriptionTracker': return premium.subscriptionTracker ? 'YES' : 'NO';
        case 'investmentTracker': return premium.investmentTracker ? 'YES' : 'NO';
        case 'displayAds': return display.hasAds ? 'YES' : 'NO';
        default: return '-';
      }
    };

    const val = getValue();
    return (
      <span className={`
        ${/NO\b/.test(val) ? 'text-red-500' : ''}
        ${/with ads/.test(val) ? 'text-green-600' : ''}
        ${val === 'Unlimited' ? 'font-semibold' : ''}
      `}>
        {val}
      </span>
    );
  };

  const features = useMemo(() => [
    { key: 'adult', label: 'Adult accounts' },
    { key: 'kids', label: 'Kids accounts' },
    { key: 'canvas', label: 'Canvas' },
    { key: 'sharedCanvas', label: 'Shared Canvas' },
    { key: 'moduleAccounts', label: 'Accounts' },
    { key: 'moduleIncome', label: 'Income' },
    { key: 'moduleBudget', label: 'Budget' },
    { key: 'moduleDebt', label: 'Debts' },
    { key: 'moduleSavings', label: 'Savings' },
    { key: 'moduleInvestment', label: 'Investment' },
    { key: 'moduleSubscriptions', label: 'Subscriptions (Canvas Module)' },
    { key: 'moduleMortgage', label: 'Mortgage (Canvas Module)' },
    { key: 'predefinedInsights', label: 'Pre-defined Insights' },
    { key: 'documents', label: 'Document Import/export' },
    { key: 'smartWidgets', label: 'Smart Widgets & AI Insights-reports' },
    { key: 'dashboard', label: 'Dashboard widgets' },
    { key: 'subscriptionTracker', label: 'Subscriptions Tracker' },
    { key: 'investmentTracker', label: 'Investment Tracker' },
    { key: 'displayAds', label: 'Display ads' }
  ], []);

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">Tier Management</h2>

      <div className="overflow-x-auto border rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-52">
                Features
              </th>
              {plans.map((plan) => (
                <th key={plan._id} className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider relative">
                  <div className="flex flex-col items-center gap-1">
                    <span>{plan.name}</span>
                    <span className="text-[10px] text-gray-400">
                      {plan.prices?.monthly?.display || (plan.prices?.monthly?.amount && `$${plan.prices.monthly.amount}/mo`) || ''}
                    </span>
                    <button
                      onClick={() => openEdit(plan)}
                      className="p-1 rounded text-btn-primary"
                      title="Edit Plan"
                    >
                      <FiEdit2 size={14} />
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {features.map((feature) => (
              <tr key={feature.key}>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {feature.label}
                </td>
                {plans.map((plan) => (
                  <td key={`${plan._id}-${feature.key}`} className="px-4 py-3 whitespace-nowrap text-sm text-center">
                    {renderValue(feature.key, plan)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="flex justify-center items-center h-16 mt-4">
          <CircularProgress size={28} />
        </div>
      )}

     {/* Ultra Compact Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-3 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold">Edit: {editingPlan?.name}</h3>
              <button onClick={closeModal} className="p-1 rounded hover:bg-gray-100" disabled={saving}>
                <FiX size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-2">
              {/* Basic Settings */}
              <div className="border rounded p-2">
                <h4 className="text-xs font-semibold mb-2">Basic Settings</h4>
                <div className="grid grid-cols-8 gap-2 mb-2">
                  <div className="col-span-2">
                    <label className="block text-xs mb-1">Name</label>
                    <input
                      className="w-full border rounded px-2 py-1 text-xs"
                      value={form.name || ''}
                      onChange={e => onChange('name', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Adults</label>
                    <input
                      type="number"
                      className="w-full border rounded px-2 py-1 text-xs"
                      value={form.adultMax || 0}
                      min="0"
                      onChange={e => onChange('adultMax', Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Kids</label>
                    <input
                      type="number"
                      className="w-full border rounded px-2 py-1 text-xs"
                      value={form.kidMax || 0}
                      min="0"
                      onChange={e => onChange('kidMax', Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Canvas</label>
                    <input
                      type="number"
                      className="w-full border rounded px-2 py-1 text-xs"
                      value={form.canvasMax || 0}
                      min="0"
                      onChange={e => onChange('canvasMax', Number(e.target.value))}
                    />
                  </div>
                  <label className="flex items-center gap-1 text-xs mt-4">
                    <input type="checkbox" checked={!!form.canvasEnabled} onChange={e => onChange('canvasEnabled', e.target.checked)} />
                    Enabled
                  </label>
                  <label className="flex items-center gap-1 text-xs mt-4">
                    <input type="checkbox" checked={!!form.canvasShared} onChange={e => onChange('canvasShared', e.target.checked)} />
                    Shared
                  </label>
                </div>
              </div>

              {/* Pricing */}
              <div className="border rounded p-2">
                <h4 className="text-xs font-semibold mb-2">Pricing</h4>
                <div className="grid grid-cols-10 gap-2">
                  <div className="col-span-2">
                    <label className="block text-xs mb-1">Monthly $</label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border rounded px-2 py-1 text-xs"
                      value={form.monthlyAmount || ''}
                      onChange={e => onChange('monthlyAmount', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs mb-1">Quarterly $</label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border rounded px-2 py-1 text-xs"
                      value={form.quarterlyAmount || ''}
                      onChange={e => onChange('quarterlyAmount', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs mb-1">Q Save</label>
                    <input
                      className="w-full border rounded px-2 py-1 text-xs bg-gray-50"
                      value={form.quarterlySave || ''}
                      readOnly
                      placeholder="Auto"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs mb-1">Yearly $</label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border rounded px-2 py-1 text-xs"
                      value={form.yearlyAmount || ''}
                      onChange={e => onChange('yearlyAmount', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs mb-1">Y Save</label>
                    <input
                      className="w-full border rounded px-2 py-1 text-xs bg-gray-50"
                      value={form.yearlySave || ''}
                      readOnly
                      placeholder="Auto"
                    />
                  </div>
                </div>
              </div>

              {/* Canvas Modules */}
              <div className="border rounded p-2">
                <h4 className="text-xs font-semibold mb-2">Canvas Modules</h4>
                <div className="grid grid-cols-8 gap-1">
                  {[
                    ['moduleAccountsEnabled', 'Accounts'],
                    ['moduleIncomeEnabled', 'Income'],
                    ['moduleBudgetEnabled', 'Budget'],
                    ['moduleDebtEnabled', 'Debts'],
                    ['moduleSavingsEnabled', 'Savings'],
                    ['moduleInvestmentEnabled', 'Investment'],
                    ['moduleSubscriptionsEnabled', 'Subs'],
                    ['moduleMortgageEnabled', 'Mortgage']
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={!!form[key]} onChange={e => onChange(key, e.target.checked)} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Tools & Premium */}
              <div className="border rounded p-2">
                <h4 className="text-xs font-semibold mb-2">Tools & Premium</h4>
                
                {/* Tools */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={!!form.insightsEnabled} onChange={e => onChange('insightsEnabled', e.target.checked)} />
                    Insights
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={!!form.documentsEnabled} onChange={e => onChange('documentsEnabled', e.target.checked)} />
                    Docs
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={!!form.documentsHasAds} onChange={e => onChange('documentsHasAds', e.target.checked)} />
                    Doc Ads
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={!!form.smartWidgetsEnabled} onChange={e => onChange('smartWidgetsEnabled', e.target.checked)} />
                    Widgets
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={!!form.smartWidgetsHasAds} onChange={e => onChange('smartWidgetsHasAds', e.target.checked)} />
                    Widget Ads
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={!!form.dashboardEnabled} onChange={e => onChange('dashboardEnabled', e.target.checked)} />
                    Dashboard
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={!!form.dashboardHasAds} onChange={e => onChange('dashboardHasAds', e.target.checked)} />
                    Dash Ads
                  </label>
                </div>

                {/* Premium */}
                <div className="grid grid-cols-5 gap-1">
                  {[
                    ['subscriptionTracker', 'Sub Track'],
                    ['investmentTracker', 'Invest Track'],
                    ['cashbot', 'Cashbot'],
                    ['premiumSmartWidgets', 'Premium'],
                    ['displayHasAds', 'Ads']
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={!!form[key]} onChange={e => onChange(key, e.target.checked)} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1 border-t">
                <button type="button" onClick={closeModal} className="px-2 py-1 text-xs rounded border hover:bg-gray-50" disabled={saving}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="px-3 py-1 text-xs rounded bg-btn-primary text-white disabled:opacity-50 flex items-center gap-1">
                  {saving && <div className="w-2 h-2 border border-white border-t-transparent rounded-full animate-spin" />}
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TierManagement;